import React, { useContext, useState, useEffect, useCallback } from 'react';
import QRCode from 'react-qr-code';
import { useNavigate } from 'react-router-dom';
import { WalletContext } from '../context/WalletContex';
import TopBar from '../components/layout/TopBar';
import { toast } from 'react-toastify';
import { HiDocumentDuplicate, HiShare, HiInformationCircle, HiShieldCheck, HiArrowPath, HiPlus, HiArrowUpRight } from 'react-icons/hi2';

const API = import.meta.env.VITE_API_URL ?? '';

const CHAINS = [
  {
    key: 'btc',
    addrKey: 'btc',
    label: 'Bitcoin',
    tab: 'BTC',
    cls: 'btc',
    icon: '₿',
    networkLabel: 'Bitcoin Network',
    networks: ['Bitcoin'],
    warning: null,
  },
  {
    key: 'usdt-trc20',
    addrKey: 'trx',
    label: 'USDT — TRON (TRC20)',
    tab: 'USDT (TRC20)',
    cls: 'usdt',
    icon: '$',
    networkLabel: 'Tron Network (TRC20)',
    networks: ['Tron'],
    warning: 'Send USDT on the Tron (TRC20) network only. Do not send BEP20 or ERC20 USDT here.',
  },
  {
    key: 'trx-native',
    addrKey: 'trx',
    label: 'TRON',
    tab: 'TRX',
    cls: 'trx',
    icon: 'T',
    networkLabel: 'Tron Network',
    networks: ['Tron'],
    warning: null,
  },
  {
    key: 'bnb',
    addrKey: 'bnb',
    label: 'BNB Smart Chain',
    tab: 'BNB',
    cls: 'eth',
    icon: 'B',
    networkLabel: 'BNB Smart Chain (BSC)',
    networks: ['BNB Smart Chain'],
    warning: 'Send only on BNB Smart Chain (BSC). Do not send ETH or any other network here.',
  },
];

export default function Receive() {
  const { wallet } = useContext(WalletContext);
  const navigate   = useNavigate();
  const [active, setActive]       = useState('btc');
  const [showNetworks, setShowNetworks] = useState(false);
  const [heldDeposits, setHeldDeposits] = useState([]);
  const [sweepEstimate, setSweepEstimate] = useState({}); // keyed by coin
  const [confirmSweep, setConfirmSweep] = useState(null); // { coin, estimate } or null
  const [sweeping, setSweeping]          = useState(false);

  const refreshHeld = useCallback(() => {
    const email = wallet?.email;
    if (!email) return;
    fetch(`${API}/api/wallet/pending-deposits?email=${encodeURIComponent(email)}`)
      .then(r => r.json())
      .then(d => setHeldDeposits(d.held ?? []))
      .catch(() => {});
  }, [wallet?.email]);

  useEffect(() => { refreshHeld(); }, [refreshHeld]);

  // Fetch sweep fee estimate per unswept coin
  useEffect(() => {
    const email = wallet?.email;
    if (!email || heldDeposits.length === 0) return;
    const RECORD_COIN = { btc: 'btc', usdt: 'usdt-trc20', trx: 'trx-native', bnb: 'bnb' };
    heldDeposits.forEach(h => {
      const rc = RECORD_COIN[h.coin];
      if (!rc) return;
      fetch(`${API}/api/wallet/sweep-estimate?email=${encodeURIComponent(email)}&coin=${rc}`)
        .then(r => r.json())
        .then(d => setSweepEstimate(prev => ({ ...prev, [h.coin]: d })))
        .catch(() => {});
    });
  }, [wallet?.email, heldDeposits.length]);

  const doSweep = async (coin) => {
    const RECORD_COIN = { btc: 'btc', usdt: 'usdt-trc20', trx: 'trx-native', bnb: 'bnb' };
    setSweeping(true);
    try {
      const r = await fetch(`${API}/api/wallet/sweep-to-pool`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: wallet.email, coin: RECORD_COIN[coin] }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      toast.success(`Swept successfully! ${d.txid ? `TX: ${d.txid.slice(0,10)}…` : 'Funds unlocked.'}`);
      setConfirmSweep(null);
      refreshHeld();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSweeping(false);
    }
  };

  const kycStatus = wallet?.kycStatus ?? 'none';
  const kycApproved = kycStatus === 'approved';

  const chain   = CHAINS.find(c => c.key === active);
  const address = wallet?.wallets?.[chain?.addrKey]?.address ?? '';

  const copy = () => {
    navigator.clipboard.writeText(address);
    toast.success('Address copied!');
  };

  const share = async () => {
    if (navigator.share) {
      await navigator.share({ title: `My Ikhua ${chain.tab} Address`, text: address });
    } else {
      copy();
    }
  };

  // KYC wall — block deposit address access until identity is verified
  if (!kycApproved) {
    const isPending  = kycStatus === 'pending';
    const isRejected = kycStatus === 'rejected';
    return (
      <div>
        <TopBar title="Receive" back />
        <div style={{ margin: '32px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 20 }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: isPending ? 'rgba(245,158,11,0.12)' : isRejected ? 'rgba(239,68,68,0.12)' : 'rgba(124,92,246,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <HiShieldCheck size={36} color={isPending ? '#F59E0B' : isRejected ? '#EF4444' : '#7C5CF6'} />
          </div>

          <div>
            <h2 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 800, color: '#fff' }}>
              {isPending ? 'Verification In Review' : isRejected ? 'Verification Rejected' : 'Verify Your Identity First'}
            </h2>
            <p style={{ margin: 0, fontSize: '14px', color: '#8B8FA8', lineHeight: 1.6, maxWidth: 300 }}>
              {isPending
                ? 'Your KYC is being reviewed. You\'ll be able to receive funds as soon as it\'s approved — usually within 24 hours.'
                : isRejected
                ? 'Your verification was rejected. Please re-submit with correct documents to unlock deposits and withdrawals.'
                : 'We need to verify your identity before you can receive funds into your account.'}
            </p>
          </div>

          {!isPending && (
            <button
              onClick={() => navigate('/profile')}
              style={{
                padding: '14px 32px', borderRadius: 14, border: 'none', cursor: 'pointer',
                background: 'linear-gradient(135deg, #7C5CF6, #5B3FD9)',
                color: '#fff', fontSize: '15px', fontWeight: 700,
              }}
            >
              {isRejected ? 'Re-submit Documents' : 'Start Verification'}
            </button>
          )}

          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: '16px', width: '100%', maxWidth: 340, textAlign: 'left' }}>
            <p style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: 700, color: '#4B5170', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Why is this required?</p>
            {[
              'Ikhua holds your funds on your behalf — we must verify who you are',
              'Required by anti-money laundering (AML) regulations',
              'Protects you and other users from fraud',
              'KYC approval is instant-to-24hrs and only done once',
            ].map((txt, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <span style={{ color: '#7C5CF6', flexShrink: 0, fontSize: '14px' }}>✓</span>
                <span style={{ fontSize: '13px', color: '#8B8FA8', lineHeight: 1.5 }}>{txt}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <TopBar title="Receive" back />

      {/* Chain selector tabs */}
      <div style={{ display: 'flex', gap: 8, padding: '8px 20px 20px', overflowX: 'auto' }}>
        {CHAINS.map(c => (
          <button
            key={c.key}
            onClick={() => { setActive(c.key); setShowNetworks(false); }}
            style={{
              flexShrink: 0,
              padding: '8px 16px',
              borderRadius: 20,
              border: '1px solid',
              borderColor: active === c.key ? 'rgba(124,92,246,0.5)' : 'rgba(255,255,255,0.07)',
              background: active === c.key ? 'rgba(124,92,246,0.12)' : 'transparent',
              color: active === c.key ? '#7C5CF6' : '#8B8FA8',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              whiteSpace: 'nowrap',
            }}
          >
            {c.tab}
          </button>
        ))}
      </div>

      {/* QR + address card */}
      <div style={{ margin: '0 16px', background: '#110E30', borderRadius: 24, border: '1px solid rgba(255,255,255,0.07)', padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>

        {/* Header */}
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className={`coin-icon ${chain.cls}`} style={{ width: 32, height: 32, borderRadius: 10, fontSize: 14 }}>
              {chain.icon}
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>{chain.label}</div>
              <div style={{ fontSize: '11px', color: '#4B5170', marginTop: 1 }}>{chain.networkLabel}</div>
            </div>
          </div>
          {/* Tap to see all supported networks */}
          {chain.networks.length > 1 && (
            <button
              onClick={() => setShowNetworks(s => !s)}
              style={{ background: 'rgba(124,92,246,0.1)', border: '1px solid rgba(124,92,246,0.2)', borderRadius: 10, padding: '5px 10px', color: '#7C5CF6', fontSize: '11px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <HiInformationCircle size={13} />
              {chain.networks.length} networks
            </button>
          )}
        </div>

        {/* Expanded network list */}
        {showNetworks && chain.networks.length > 1 && (
          <div style={{ width: '100%', background: 'rgba(124,92,246,0.06)', border: '1px solid rgba(124,92,246,0.14)', borderRadius: 14, padding: '12px 14px' }}>
            <p style={{ fontSize: '12px', fontWeight: 700, color: '#7C5CF6', margin: '0 0 8px' }}>
              This address works on all these networks:
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {chain.networks.map(n => (
                <span key={n} style={{ fontSize: '11px', fontWeight: 600, color: '#A78BFA', background: 'rgba(124,92,246,0.12)', borderRadius: 20, padding: '3px 10px' }}>
                  {n}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* QR */}
        <div className="qr-wrapper">
          <QRCode value={address || 'ikhua'} size={180} level="M" />
        </div>

        {/* Network warning */}
        {chain.warning && (
          <div className="warning-banner" style={{ width: '100%' }}>
            <span style={{ fontSize: 15 }}>⚠️</span>
            <span style={{ fontSize: '12px', color: '#F59E0B', lineHeight: 1.5 }}>{chain.warning}</span>
          </div>
        )}

        {/* Address */}
        <div style={{ width: '100%' }}>
          <p style={{ fontSize: '12px', color: '#4B5170', marginBottom: 6, fontWeight: 600 }}>Your address</p>
          <div className="address-box">{address || 'Loading…'}</div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, width: '100%' }}>
          <button
            onClick={copy}
            style={{ flex: 1, height: 48, borderRadius: 14, background: 'rgba(124,92,246,0.12)', border: '1px solid rgba(124,92,246,0.25)', color: '#7C5CF6', fontSize: '14px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            <HiDocumentDuplicate size={16} /> Copy
          </button>
          <button
            onClick={share}
            style={{ flex: 1, height: 48, borderRadius: 14, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            <HiShare size={16} /> Share
          </button>
        </div>
      </div>

      {/* Summary info box */}
      <div style={{ margin: '12px 16px 0', padding: '14px', background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.12)', borderRadius: 14 }}>
        {active === 'usdt-trc20' ? (
          <p style={{ color: '#10B981', fontSize: '13px', margin: 0, lineHeight: 1.6 }}>
            <strong>Tron (TRC20) address.</strong> Use this when someone sends USDT from Binance, OKX, or any exchange that supports TRC20 — cheapest network for USDT.
          </p>
        ) : active === 'trx-native' ? (
          <p style={{ color: '#10B981', fontSize: '13px', margin: 0, lineHeight: 1.6 }}>
            <strong>Tron address.</strong> Share this to receive TRX native. Funds arrive within seconds on the Tron network.
          </p>
        ) : active === 'bnb' ? (
          <p style={{ color: '#10B981', fontSize: '13px', margin: 0, lineHeight: 1.6 }}>
            <strong>BNB Smart Chain address.</strong> Share this to receive BNB. Always confirm the sender is on BSC — not Ethereum or any other network.
          </p>
        ) : (
          <p style={{ color: '#10B981', fontSize: '13px', margin: 0, lineHeight: 1.6 }}>
            <strong>Bitcoin address.</strong> Share this to receive BTC. Funds arrive after 1–3 network confirmations (~10–30 min).
          </p>
        )}
      </div>

      {/* On-chain unswept — credited to balance, not yet available for trading */}
      {heldDeposits.length > 0 && (
        <div style={{ margin: '12px 16px 32px' }}>
          <p style={{ fontSize: '12px', fontWeight: 700, color: '#4B5170', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 10px' }}>
            Balance Pending Sweep
          </p>

          {heldDeposits.map(h => {
            const est = sweepEstimate[h.coin];
            const fmtAmt = n => Number(n).toLocaleString(undefined, { maximumFractionDigits: 8 });
            return (
              <div key={h.coin} style={{ background: '#0F0D2A', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 18, padding: '18px', marginBottom: 12 }}>

                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: '13px', fontWeight: 800, color: '#F59E0B', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                    {h.coin.toUpperCase()} — On-chain
                  </span>
                  <span style={{ fontSize: '16px', fontWeight: 800, color: '#fff' }}>
                    {fmtAmt(h.totalAmount)} {h.coin.toUpperCase()}
                  </span>
                </div>

                <p style={{ fontSize: '12px', color: '#8B8FA8', margin: '0 0 16px', lineHeight: 1.6 }}>
                  This is in your balance but still on-chain. You cannot trade, swap, or withdraw it until it's swept into the pool.
                </p>

                {/* Option 1 — Sweep Now */}
                <button
                  onClick={() => setConfirmSweep({ coin: h.coin, estimate: est, held: h })}
                  style={{ width: '100%', padding: '12px 16px', marginBottom: 8, borderRadius: 13, border: '1px solid rgba(124,92,246,0.35)', background: 'rgba(124,92,246,0.1)', color: '#fff', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12 }}
                >
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(124,92,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <HiArrowPath size={18} color="#A78BFA" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', marginBottom: 2 }}>Sweep Now — unlock for trading</div>
                    <div style={{ fontSize: '11px', color: '#8B8FA8' }}>
                      {est ? `~${fmtAmt(est.estimatedGasFee)} ${est.gasFeeCoin?.toUpperCase()} gas fee · you pay` : 'Calculating gas fee…'}
                    </div>
                  </div>
                  <span style={{ fontSize: '11px', color: '#A78BFA', fontWeight: 700 }}>Instant</span>
                </button>

                {/* Option 2 — Deposit more */}
                <button
                  onClick={() => { window.scrollTo(0, 0); }}
                  style={{ width: '100%', padding: '12px 16px', marginBottom: 8, borderRadius: 13, border: '1px solid rgba(16,185,129,0.25)', background: 'rgba(16,185,129,0.06)', color: '#fff', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12 }}
                >
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <HiPlus size={18} color="#10B981" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', marginBottom: 2 }}>
                      Deposit {h.needed > 0 ? `${fmtAmt(h.needed)} more ${h.coin.toUpperCase()}` : 'more'}
                    </div>
                    <div style={{ fontSize: '11px', color: '#8B8FA8' }}>
                      {h.needed > 0
                        ? `Reach sweep threshold — platform covers gas fee`
                        : 'Sweep triggers automatically next cycle — free'}
                    </div>
                  </div>
                  <span style={{ fontSize: '11px', color: '#10B981', fontWeight: 700 }}>Free</span>
                </button>

                {/* Option 3 — Send to external wallet */}
                <button
                  onClick={() => navigate('/send')}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: 13, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', color: '#fff', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12 }}
                >
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <HiArrowUpRight size={18} color="#8B8FA8" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', marginBottom: 2 }}>Withdraw to external wallet</div>
                    <div style={{ fontSize: '11px', color: '#8B8FA8' }}>Standard withdrawal fee applies</div>
                  </div>
                  <span style={{ fontSize: '11px', color: '#8B8FA8', fontWeight: 700 }}>Fee</span>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Sweep confirm modal */}
      {confirmSweep && (() => {
        const { coin, estimate, held } = confirmSweep;
        const fmtAmt = n => Number(n).toLocaleString(undefined, { maximumFractionDigits: 8 });
        const gasFee  = estimate?.estimatedGasFee ?? 0;
        const gasCoin = estimate?.gasFeeCoin?.toUpperCase() ?? coin.toUpperCase();
        const netAmt  = estimate?.netAfterFee ?? held.totalAmount;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
            onClick={e => { if (e.target === e.currentTarget && !sweeping) setConfirmSweep(null); }}>
            <div style={{ background: '#110E30', borderRadius: '24px 24px 0 0', padding: '28px 24px 40px', width: '100%', maxWidth: 430 }}>
              <div style={{ width: 40, height: 4, background: 'rgba(255,255,255,0.15)', borderRadius: 2, margin: '0 auto 24px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#fff', margin: '0 0 6px' }}>Confirm Sweep</h3>
              <p style={{ fontSize: '13px', color: '#8B8FA8', margin: '0 0 24px', lineHeight: 1.6 }}>
                Your {coin.toUpperCase()} will be moved on-chain from your deposit address into the trading pool. This is irreversible.
              </p>

              <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: '16px', marginBottom: 20 }}>
                {[
                  ['Amount to sweep', `${fmtAmt(held.totalAmount)} ${coin.toUpperCase()}`],
                  ['On-chain gas fee (you pay)', `~${fmtAmt(gasFee)} ${gasCoin}`],
                  ['Net unlocked for trading', `≈${fmtAmt(netAmt)} ${coin.toUpperCase()}`],
                ].map(([label, value]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, lastChild: { marginBottom: 0 } }}>
                    <span style={{ fontSize: '12px', color: '#8B8FA8' }}>{label}</span>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: label.includes('gas fee') ? '#F59E0B' : '#fff' }}>{value}</span>
                  </div>
                ))}
              </div>

              <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 12, padding: '10px 14px', marginBottom: 20, display: 'flex', gap: 10 }}>
                <span style={{ fontSize: 14 }}>⚠️</span>
                <span style={{ fontSize: '12px', color: '#F59E0B', lineHeight: 1.55 }}>
                  Gas fee is an estimate. Actual fee is deducted at time of sweep based on current network conditions. The difference is small but may vary.
                </span>
              </div>

              <button
                onClick={() => doSweep(coin)}
                disabled={sweeping}
                style={{ width: '100%', height: 52, borderRadius: 14, border: 'none', background: sweeping ? 'rgba(124,92,246,0.4)' : 'linear-gradient(135deg,#7C5CF6,#5B3FD9)', color: '#fff', fontSize: '15px', fontWeight: 700, cursor: sweeping ? 'default' : 'pointer', marginBottom: 10 }}
              >
                {sweeping ? 'Sweeping…' : 'Confirm Sweep'}
              </button>
              <button
                onClick={() => setConfirmSweep(null)}
                disabled={sweeping}
                style={{ width: '100%', height: 44, borderRadius: 14, border: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: '#8B8FA8', fontSize: '14px', cursor: sweeping ? 'default' : 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
